import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card } from 'src/components/Card';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import {
  FranchiseFeePayersQueryKey,
  fetchFranchiseFeePayers,
} from 'src/models/hq/franchise-fee-payer/franchise-fee-payer';
import {
  FranchiseFeePayout,
  FranchiseFeePayoutsQueryKey,
  initiatePayout,
} from 'src/models/hq/franchise-fee-payout';
import { formatMonthYear } from 'src/utils/dateFormatter';
import { formatCurrency } from 'src/utils/numberFormatter';
import toast from 'src/utils/toast';

import { PayerDetails } from './PayerDetails';

interface PayoutInitiateDialogProps {
  open: boolean;
  onClose: () => void;
  payout: FranchiseFeePayout | null;
  onInitiatePayout?: () => void;
}

export function PayoutInitiateDialog({
  open,
  onClose,
  payout,
  onInitiatePayout,
}: PayoutInitiateDialogProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'franchiseFees.payouts.initiateDialog',
  });
  const queryClient = useQueryClient();
  const buttonText = payout?.payoutStatus === 'failed' ? t('retryPayout') : t('initiatePayout');
  // Fetch all payers if payer is not set on the payout
  const { data: payersData, isLoading: isLoadingPayers } = useQuery({
    queryKey: [FranchiseFeePayersQueryKey, { fetchAll: true }],
    queryFn: () =>
      fetchFranchiseFeePayers({
        page: 1,
        perPage: 1000, // Fetch all payers
      }),
    enabled: open && !!payout && !payout.payerId,
  });

  // Find the matching payer based on payerPlatformId
  const payerPlatformId = payout?.payerPlatformId;
  const payoutClosed= payout?.payoutStatus === 'in_progress' || payout?.payoutStatus === 'completed'
  const matchedPayer = payersData?.franchiseFeePayers.find((payer) => {
    if (payoutClosed) {
      return payer.id === payout.payerId;
    }
    return payerPlatformId ? payer.platformIds.includes(payerPlatformId) : false;
  });

  // Determine which payer information to display
  const payerName = payout?.payerId
    ? payout.payerName || payout.payerPlatformName || 'Unknown Payer'
    : matchedPayer
    ? matchedPayer.name
    : null;

  const isLoading = isLoadingPayers && !payout?.payerId;

  // Mutation for initiating payout
  const mutation = useMutation({
    mutationFn: () =>
      initiatePayout(
        payout?.id as string,
        matchedPayer?.id as string,
      ),
    onSuccess: () => {
      // Invalidate and refetch payout queries
      queryClient.invalidateQueries({ queryKey: [FranchiseFeePayoutsQueryKey] });
      queryClient.invalidateQueries({ queryKey: ['franchise-fee-payout', String(payout?.id)] });
      // 'franchise-fee-payout', id

      // Call success callbacks
      onInitiatePayout?.();
      onClose();

      // Show success message
      toast.success(t('payoutInitiatedSuccessfully'));
    },
    onError: (error: any) => {
      // Show error message but keep dialog open
      const errorMessage = error?.response?.data?.error || error?.message || t('payoutInitiationFailed');
      toast.error(errorMessage);
    },
  });

  const initiatePayoutHandler = () => {
    mutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title')}
      maxWidth="sm"
      actions={[
        {
          label: t('cancel'),
          onClick: onClose,
          variant: 'outlined',
          disabled: mutation.isPending,
        },
        {
          label: buttonText,
          onClick: initiatePayoutHandler,
          variant: 'contained',
          color: 'primary',
          disabled: !payerName || isLoading || mutation.isPending,
          loading: mutation.isPending,
        },
      ]}
    >
      {!payout ? (
        <Typography>{t('noPayoutSelected')}</Typography>
      ) : (
        <Stack spacing={3}>
          {/* Amount Owed Section */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('amountOwed')}
            </Typography>
            <Typography variant="h4" fontWeight={600} color="primary">
              {formatCurrency(payout.amountOwedInDollars || 0)}
            </Typography>
          </Box>
          <Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
            <Typography variant="body2" gutterBottom>
              {t('PayerPlatform')}
            </Typography>
            <Typography variant="body1">
              {payout.payerPlatformName}
            </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>

          <Typography variant="body2" gutterBottom>
              {t('period')}
            </Typography>
            <Typography variant="body1">
              {formatMonthYear(payout.payoutPeriodStart, 'UTC')}
            </Typography>
              </Grid>
              </Grid>
          </Box>

          {/* Payer Information Section */}
          <Box>
            <Card sx={{ p: 3 }}>
              {<PayerDetails payer={matchedPayer} isLoading={isLoading} />}
            </Card>
          </Box>
        </Stack>
      )}
    </Dialog>
  );
}

